import React, { Component } from "react";
import MaskedInput from "react-text-mask";
import { OrganizationService, AutoAssignmentLogService } from "@services";
import utils from "@utils";
import { AsyncSelect } from "../../../../../components";

import "./AssignmentAudit.scss";

const dateMask = [/[0-1]/, /[0-9]/, "/", /[0-3]/, /[0-9]/, "/", /2/, /0/, /[2-9]/, /[0-9]/];
class AssignmentAudit extends Component {
	constructor(props) {
		super(props);

		this.state = {
			organizationId: "",
			jobEventId: "",
			jobEventRecurrenceRuleId: "",
			jobEventStartDateTime: "",
			jobEventEndDateTime: "",
			reportData: undefined,
		};
	}

	/**
	 * Finds organizations to perform an assignment audit for whose names contain the given text
	 * @param {String} input The text used to search for an organization
	 * @param {Function} callback A callback function used to handle the results of the organization search
	 */
	findOrganizations = (input, callback) => {
		if (input.length < 3) {
			callback([]);
			return;
		}
		OrganizationService.getAllDetailed({ pageNumber: 1, name: input })
			.then(({ data: { ItemList } }) => {
				// Filter all Client organizations since they'll never return any results no matter what
				callback(
					ItemList.filter(({ Type }) => Type !== 20).map(
						({ Id: value, Name: label }) => ({ value, label }),
					),
				);
			})
			.catch(() => callback([]));
	};

	handleOrgChange = event => {
		this.setState({ organizationId: event?.value || "" });
	};

	/**
	 * Handles all HTML input changes
	 * @param {String} property The property to update when this method is invoked
	 */
	handleChange =
		property =>
		({ target: { type, value } }) => {
			let formattedValue = value;
			if (type === "tel") formattedValue = value.replace(/[^0-9]/gi, "");
			this.setState({ [property]: formattedValue });
		};

	/** Calls the API service to retrieve auto-assignment audit logs for the selected organization */
	handleSubmit = () => {
		const {
			organizationId,
			jobEventId,
			jobEventRecurrenceRuleId,
			jobEventStartDateTime,
			jobEventEndDateTime,
		} = this.state;

		let queryParams = {
			organizationId,
			jobEventId,
			jobEventRecurrenceRuleId,
			jobEventStartDateTime,
			jobEventEndDateTime,
		};
		queryParams = Object.fromEntries(Object.entries(queryParams).filter(([, value]) => value));

		AutoAssignmentLogService.getAll(queryParams)
			.then(({ data }) => this.setState({ reportData: data }))
			.catch(() => this.setState({ reportData: [] }));
	};

	render() {
		const { lang } = global;
		const {
			organizationId,
			jobEventId,
			jobEventRecurrenceRuleId,
			jobEventStartDateTime,
			jobEventEndDateTime,
			reportData,
		} = this.state;

		const params = {
			organizationId,
			jobEventId,
			jobEventRecurrenceRuleId,
			jobEventStartDateTime,
			jobEventEndDateTime,
		};

		const buttonDisabled =
			Object.values(params).filter(x => x).length < 2 ||
			(Object.values(params).filter(x => x).length >= 2 && params.organizationId === "");

		return (
			<div
				id="assignment-audit"
				className="container"
			>
				<div className="row">
					<div className="col-sm-12">
						<h2>{lang.t("Assignment Audit")}</h2>
					</div>
				</div>
				<div className="row">
					<p className="col-sm-12">
						{lang.t(
							"Select an organization and provide at least one other value to search",
						)}
					</p>
				</div>
				<div className="row">
					<div className="col-sm-3 form-group">
						<label htmlFor="audit-org">
							{lang.t("Organization")}
							<span className="required-mark" />
						</label>
						<AsyncSelect
							name="audit-org"
							id="audit-org"
							placeholder="Type to search"
							cacheOptions
							loadOptions={this.findOrganizations}
							onChange={this.handleOrgChange}
							isClearable
						/>
					</div>
				</div>
				<div className="row">
					<div className="col-sm-3 form-group">
						<label htmlFor="jobid-search">{lang.t("Job ID")}</label>
						<input
							type="tel"
							id="jobid-search"
							name="jobid-search"
							className="form-control"
							onChange={this.handleChange("jobEventId")}
							onKeyPress={({ key }) => {
								if (key === "Enter") this.handleSubmit();
							}}
							value={jobEventId}
						/>
					</div>
					<div className="col-sm-3 form-group">
						<label htmlFor="reccurenceid-search">{lang.t("Recurrence ID")}</label>
						<input
							type="tel"
							id="recurrenceid-search"
							name="recurrenceid-search"
							className="form-control"
							onChange={this.handleChange("jobEventRecurrenceRuleId")}
							onKeyPress={({ key }) => {
								if (key === "Enter") this.handleSubmit();
							}}
							value={jobEventRecurrenceRuleId}
						/>
					</div>
					<div className="col-sm-3 form-group">
						<label htmlFor="start-from">
							{lang.t("Date Range")} {lang.t("Start")}
						</label>
						<Choose>
							<When condition={utils.isDesktopSafari || utils.isIE}>
								<MaskedInput
									name="start-from"
									id="start-from"
									type="text"
									mask={dateMask}
									className="form-control"
									placeholder="mm/dd/yyyy"
									value={jobEventStartDateTime}
									onChange={this.handleChange("jobEventStartDateTime")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.handleSubmit();
									}}
								/>
							</When>
							<Otherwise>
								<input
									name="start-from"
									id="start-from"
									type="date"
									className="form-control"
									value={jobEventStartDateTime}
									onChange={this.handleChange("jobEventStartDateTime")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.handleSubmit();
									}}
								/>
							</Otherwise>
						</Choose>
					</div>
					<div className="col-sm-3 form-group">
						<label htmlFor="start-from">
							{lang.t("Date Range")} {lang.t("End")}
						</label>
						<Choose>
							<When condition={utils.isDesktopSafari || utils.isIE}>
								<MaskedInput
									name="end-on"
									id="end-on"
									type="text"
									mask={dateMask}
									className="form-control"
									placeholder="mm/dd/yyyy"
									value={jobEventEndDateTime}
									onChange={this.handleChange("jobEventEndDateTime")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.handleSubmit();
									}}
								/>
							</When>
							<Otherwise>
								<input
									name="end-on"
									id="end-on"
									type="date"
									className="form-control"
									value={jobEventEndDateTime}
									onChange={this.handleChange("jobEventEndDateTime")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.handleSubmit();
									}}
								/>
							</Otherwise>
						</Choose>
					</div>
				</div>
				<div className="row">
					<div className="col-sm-12">
						<button
							type="button"
							className="btn btn-primary"
							disabled={buttonDisabled}
							onClick={this.handleSubmit}
						>
							{lang.t("Audit")}
						</button>
					</div>
				</div>
				<div className="row">
					<div className="col-sm-12">
						<Choose>
							<When condition={reportData && reportData.length === 0}>
								<div className="text-center text-muted">
									{lang.t("No records found")}
								</div>
							</When>
							<When condition={reportData && reportData.length > 0}>
								<table className="table">
									<thead>
										<tr>
											<th>{lang.t("Job")}</th>
											<th>{lang.t("Officer")}</th>
											<th>
												{lang.t("Job")} {lang.t("Start")}
											</th>
											<th>
												{lang.t("Job")} {lang.t("End")}
											</th>
											<th>
												{lang.t("Shift")} {lang.t("Start")}
											</th>
											<th>
												{lang.t("Shift")} {lang.t("End")}
											</th>
											<th>{lang.t("Type")}</th>
											<th>{lang.t("QC #")}</th>
											<th>{lang.t("QC Start")}</th>
											<th>{lang.t("QC Days")}</th>
											<th>{lang.t("QC Max Assignments")}</th>
											<th>
												{lang.t("Assignment")} {lang.t("Order")}
											</th>
											<th>{lang.t("Assigned")}</th>
										</tr>
									</thead>
									<tbody>
										<For
											each="item"
											of={reportData}
										>
											<tr key={`logitem-${item.Id}`}>
												<td>
													<a
														href={`/jobs/${item.JobEventId}`}
														target="_blank"
														rel="noopener noreferrer"
													>
														{item.JobEventTitle} (#{item.JobEventId})
													</a>
												</td>
												<td>
													{`${item.OfficerFirstName} ${item.OfficerLastName}`}
													<br />
													<a href={`mailto:${item.OfficerEmailAddress}`}>
														{item.OfficerEmailAddress}
													</a>
												</td>
												<td>
													{utils.formatDate(
														`${item.JobEventStartDateTime}Z`,
														"en-US",
														undefined,
														"dt-num-tz",
													)}{" "}
												</td>
												<td>
													{utils.formatDate(
														`${item.JobEventEndDateTime}Z`,
														"en-US",
														undefined,
														"dt-num-tz",
													)}
												</td>
												<td>
													{utils.formatDate(
														`${item.JobStartDateTime}Z`,
														"en-US",
														undefined,
														"dt-num-tz",
													)}
												</td>
												<td>
													{utils.formatDate(
														`${item.JobEndDateTime}Z`,
														"en-US",
														undefined,
														"dt-num-tz",
													)}
												</td>
												<td>{item.AutoAssignDescription}</td>
												<td>{item.QueueCycleNumber || ""}</td>
												<td>
													{item.QueueCycleStartDate
														? utils.formatDate(
																`${item.QueueCycleStartDate}`,
																"en-US",
																undefined,
																"dt-num-tz",
															)
														: ""}
												</td>
												<td>{item.QueueCycleCycleDays || ""}</td>
												<td>{item.QueueCycleMaxAssignments || ""}</td>
												<td>
													{item.AutoAssignOrderNumber}
													<br />
													<small>{item.AutoAssignOrderValue}</small>
												</td>
												<td className="text-center">
													<If condition={item.Assigned}>
														<i className="glyphicon glyphicon-ok text-success" />
													</If>
												</td>
											</tr>
										</For>
									</tbody>
								</table>
							</When>
						</Choose>
					</div>
				</div>
			</div>
		);
	}
}

export default AssignmentAudit;
